import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, Box, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StudentForm from './StudentForm';
import { studentsApi } from './api';

// Helper to remove empty strings/nulls to prevent backend validation errors
const cleanEmptyFields = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(v => cleanEmptyFields(v));
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const value = obj[key];
      // Keep booleans (false is valid), numbers (0 is valid)
      // Remove empty strings, null, undefined
      if (value === '' || value === null || value === undefined) {
        return acc;
      }
      acc[key] = cleanEmptyFields(value);
      return acc;
    }, {} as any);
  }
  return obj;
};

const StudentCreate: React.FC = () => {
  const navigate = useNavigate();

  const normalizeStudentPayload = (values: any) => {
    return {
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      dateOfBirth: values.dateOfBirth,
      placeOfBirth: values.placeOfBirth,
      gender: values.gender,
      bloodGroupId: values.bloodGroupId,
      examBoardId: values.examBoardId,
      aadharNumber: values.aadharNumber,
      status: values.status,
      admissionNumber: values.admissionNumber,
      scholarNumber: values.scholarNumber,
      classId: values.classId,
      sectionId: values.sectionId,
      academicSessionId: values.academicSessionId,
      admissionDate: values.admissionDate,
      mediumId: values.mediumId,
      rte: values.rte,
      bpl: values.bpl,
      casteId: values.casteId,
      categoryId: values.categoryId,
      religionId: values.religionId,
      houseId: values.houseId,
      nationalityId: values.nationalityId,
      motherTongueId: values.motherTongueId,
      studentTypeId: values.studentTypeId,
      studentPhotoUrl: values.studentPhotoUrl,
      fatherPhotoUrl: values.fatherPhotoUrl,
      motherPhotoUrl: values.motherPhotoUrl,
      guardianPhotoUrl: values.guardianPhotoUrl,
      remarks: values.remarks,
      isDeclared: values.isDeclared,
      isPermanentSameAsPresent: values.isPermanentSameAsPresent,

      parent: values.parent ? {
        fatherName: values.parent.fatherName,
        fatherMobile: values.parent.fatherMobile,
        fatherOccupationId: values.parent.fatherOccupationId,
        fatherQualification: values.parent.fatherQualification,
        fatherAnnualIncome: values.parent.fatherAnnualIncome,
        motherName: values.parent.motherName,
        motherMobile: values.parent.motherMobile,
        motherOccupationId: values.parent.motherOccupationId,
        motherQualification: values.parent.motherQualification,
        motherAnnualIncome: values.parent.motherAnnualIncome,
        guardianName: values.parent.guardianName,
        guardianMobile: values.parent.guardianMobile,
        guardianRelationId: values.parent.guardianRelationId,
        guardianAddress: values.parent.guardianAddress,
        guardianEmail: values.parent.guardianEmail,
      } : undefined,

      addresses: values.addresses?.map((a: any) => ({
        type: a.type,
        addressLine1: a.addressLine1,
        addressLine2: a.addressLine2,
        city: a.city,
        state: a.state,
        pincode: a.pincode,
        district: a.district,
        country: a.country
      })),

      previousSchool: values.previousSchool || undefined,

      documents: values.documents?.map((d: any) => ({
        documentId: d.documentId,
        name: d.name,
        url: d.url
      }))
    };
  };

  const handleSubmit = async (values: any) => {
    // 1. Normalize and Clean Payload
    const normalizedPayload = normalizeStudentPayload(values);
    const cleanValues = cleanEmptyFields(normalizedPayload);

    try {
      await studentsApi.create(cleanValues);
      navigate('/scholar-register/students');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create student';
      alert(`Error: ${errorMessage}`);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/scholar-register/students')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Add New Student
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <StudentForm onSubmit={handleSubmit} />
      </Paper>
    </Container>
  );
};

export default StudentCreate;
